import { useI18n } from "@core/i18n/i18n";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";
import { PendingPatient } from "@domain/patient/patient";
import { sendAnalyticsEventReinvitePatient } from "@domain/patient/patient-analytics";
import { AnalyticsProgramClickType, sendAnalyticsEventViewPatientDetail } from "@domain/program/program-analytics";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { Tooltip } from "@ui/components/common/tooltip";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { ExpirationRemainingDays } from "@ui/pages/home/components/expiration-remaining-days";
import { PatientActionButton } from "@ui/pages/home/components/patient-action-button";
import { PendingPatientListingRowMobile } from "@ui/pages/home/components/pending-patient-listing-row-mobile";
import { ResendCodeButton } from "@ui/pages/home/components/resend-code-button";
import { DialogStep } from "@ui/pages/home/pending-patient-dialog/add-patient-dialog";
import {
	EmptyPatientMessage,
	EmptyPatientMessageVariant,
} from "@ui/pages/patient-details/components/empty-patient-message";
import { Column } from "primereact/column";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import React, { useContext } from "react";

export type ExtendedPendingPatient = PendingPatient & {
	name?: string;
};

const expirationBody = (data: ExtendedPendingPatient) => (
	<ExpirationRemainingDays patient={data} />
);

const patientActionBody = (data: ExtendedPendingPatient) => (
	<PatientActionButton patient={data} />
);

const ResendCodeCell: React.FC<{ patient: ExtendedPendingPatient }> = ({ patient }) => {
	const dialogContext = useContext(DialogContext);
	return (
		<ResendCodeButton
			onClick={() => {
				sendAnalyticsEventReinvitePatient();
				dialogContext?.openModal(ModalId.AddPatient, { pendingPatient: patient });
			}}
		/>
	);
};

const resendCodeBody = (data: ExtendedPendingPatient) => <ResendCodeCell patient={data} />;

interface PendingPatientProps {
	pendingPatients: PendingPatient[];
}

export const PendingPatientListing: React.FC<PendingPatientProps> = ({ pendingPatients }) => {
	const { format, formatDateMed } = useI18n();
	const { formatPatientName } = useFormatPatients();
	const dialogContext = useContext(DialogContext);

	const columnProps = { sortable: true };
	const pendingPatientStatsRows = pendingPatients.map(
		(patient): ExtendedPendingPatient => ({
			...patient,
			name: formatPatientName(patient),
		}),
	);

	const { isDesktop } = useDimensions();

	if (!isDesktop) {
		return pendingPatientStatsRows.length === 0 ? (
			<EmptyPatientMessage variant={EmptyPatientMessageVariant.NoPendingPatients} />
		) : (
			pendingPatientStatsRows.map(patient => <PendingPatientListingRowMobile key={patient.id} patient={patient} />)
		);
	}
	return (
		<DataTable
			className="p-round-corner"
			dataKey="activationCode"
			tableStyle={{ width: "100%" }}
			value={pendingPatientStatsRows}
			rowHover={true}
			onRowClick={(e: DataTableRowClickEvent) => {
				const data = e.data as ExtendedPendingPatient;
				sendAnalyticsEventViewPatientDetail(data.analyticsId, AnalyticsProgramClickType.LIST_CLICK);
				dialogContext?.openModal(ModalId.AddPatient, {
					pendingPatient: data,
					step: DialogStep.RegistrationDetails,
				});
			}}
		>
			<Column
				{...columnProps}
				field="name"
				bodyStyle={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}
				bodyClassName="p-size-s-bold"
				header={format("home.pendingPatientsListing.patientNameColumn")}
				body={({ name }: ExtendedPendingPatient) => name}
			/>
			<Column
				{...columnProps}
				field="activationCode"
				header={format("home.pendingPatientsListing.codeColumn")}
				body={({ activationCode }: ExtendedPendingPatient) => activationCode}
			/>
			<Column
				{...columnProps}
				field="creationDate"
				header={format("home.pendingPatientsListing.creationDateColumn")}
				body={(data: ExtendedPendingPatient) => formatDateMed(data.creationDate)}
			/>
			<Column
				{...columnProps}
				field="expiration"
				header={
					<Tooltip
						content={format("home.pendingPatientsListing.expiresInColumn.tooltip")}
						maxWidth={400}
						placement="top"
					>
						<span className="p-column-title last-completed-session">
							{format("home.pendingPatientsListing.expiresInColumn")}
						</span>
					</Tooltip>
				}
				body={expirationBody}
			/>
			<Column
				field="resendCode"
				body={resendCodeBody}
			/>
			<Column
				field="additionalOptions"
				style={{ width: 72 }}
				body={patientActionBody}
			/>
		</DataTable>
	);
};
